package fr.frux.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import fr.frux.feature.map.MapHostState

/**
 * Applique un App Link reçu à chaud (#704). La carte est une surface persistante (ADR 0009) :
 * un lien `/carte` ramène la pile à elle et recentre son ViewModel, sans jamais la reconstruire.
 *
 * Deux identifiants mémorisés plutôt qu'un seul : la carte peut ne pas exister au moment du lien
 * (elle ne démarre qu'à la première visite d'un écran qui en a besoin), la navigation est alors
 * faite tout de suite et le recentrage attend `mapHost`, ce qui relance l'effet.
 */
@Composable
internal fun AppLinkEffect(
    navigationState: NavigationState,
    appLink: AppLink?,
    mapHost: MapHostState?,
) {
    var navigatedId by remember { mutableStateOf<Long?>(null) }
    var viewShownId by remember { mutableStateOf<Long?>(null) }
    LaunchedEffect(navigationState, appLink, mapHost != null) {
        val link = appLink ?: return@LaunchedEffect
        when (val action = link.toAction()) {
            is AppLinkAction.Open -> {
                if (navigatedId != link.id) {
                    navigatedId = link.id
                    navigationState.navigate(action.route)
                }
            }

            is AppLinkAction.ShowMap -> {
                if (navigatedId != link.id) {
                    navigatedId = link.id
                    navigationState.showMapForLink()
                }
                val view = action.view
                if (view != null && mapHost != null && viewShownId != link.id) {
                    viewShownId = link.id
                    mapHost.viewModel.showLinkedView(view)
                }
            }

            AppLinkAction.Ignore -> {
                Unit
            }
        }
    }
}

/** Accueil et connexion restent en place : sauter l'onboarding n'est pas le rôle d'un lien. */
private fun NavigationState.showMapForLink() {
    val current = backStack.lastOrNull()
    if (current != null && current !in setOf(Route.Map, Route.Welcome, Route.Auth)) resetTo(Route.Map)
}
